<template>
	<div ref="heroSectionRef" class="hero-section full-bleed">
		<Splide
			v-focus-section
			ref="splideRef"
			:options="{
				// inteval: 10000,
				type: 'loop',
				// autoplay: true,
				perPage: 1,
				noDrag: true,
				arrows: false,
				pagination: true,
			}"
			@splide:pagination:updated="handlePaginationUpdate"
			@splide:pagination:mounted="placePagination"
			aria-label="Hero Section"
		>
			<SplideSlide v-focus v-for="(item, key) in heroData" :key="key" @click="openInnerPage(item)">
				<TeaserHero :item="item" :hideImage="hideImage" />
			</SplideSlide>
		</Splide>
		<div class="player-wrapper">
			<div ref="playerRef" :id="'kwik-player-hero'" class="kwik-player trailer"></div>
		</div>

		<!-- <div class="splide__arrows">
			<button v-focus @click="prevSlide(splideRef)" class="splide__arrow splide__arrow--prev"><IconArrow /></button>
			<button v-focus @click="nextSlide(splideRef)" class="splide__arrow splide__arrow--next">
				<IconArrow />
			</button>
		</div> -->
	</div>
</template>
<script lang="ts" setup>
	import { ref, computed, onBeforeMount, onMounted, onBeforeUnmount, ComputedRef, watch, nextTick } from "vue"
	import { useMovieStore } from "../../stores/movies"
	import { useSeriesStore } from "../../stores/series"
	import { useSplide } from "../../composables/useSplide"
	import TeaserHero from "../teasers/TeaserHero.vue"
	import IconArrow from "../../icons/IconArrow.vue"
	import { useRouter, useRoute, onBeforeRouteLeave } from "vue-router"
	import { Movie } from "../../types/Movie"
	import { Series } from "../../types/Series"
	import { url } from "../../helper"
	import { useTeaserStore } from "../../stores/teaser"
	import api from "../../composables/useApi"
	const teaserStore = useTeaserStore()
	const { nextSlide, prevSlide } = useSplide()
	const router = useRouter()
	const route = useRoute()
	const splideRef = ref(undefined)
	const hideImage = ref(false)
	const currentSlideIndex = ref(0)
	const heroSectionRef = ref(null)
	let player: any = null
	let observer: IntersectionObserver | null = null
	const containerId = `kwik-player-hero`
	let options: any = {}
	let endedHandler: (() => void) | null = null

	// Fetch hero movies and series.
	const movieStore = useMovieStore()
	const seriesStore = useSeriesStore()

	const emit = defineEmits<{
		(e: "heroLoaded"): void
		(e: "activeItemChanged", item: Movie | Series): void
	}>()

	// Place pagination safely after DOM update
	const placePagination = async () => {
		await nextTick()
		const pagination = document.querySelector(".splide__pagination")
		const customContainer = document.getElementById("custom-pagination")
		if (!pagination || !customContainer) {
			console.warn("Pagination or custom container not found yet")
			return
		}
		customContainer.appendChild(pagination)
		pagination.classList.add("placed")
	}

	onBeforeMount(async () => {
		// Fetch based on current route
		if (route.name === "MoviesPage") {
			await movieStore.getHeroMovies()
		} else if (route.name === "SeriesPage") {
			await seriesStore.getHeroSeries()
		} else {
			// Home route - fetch both
			await movieStore.getHeroMovies()
			await seriesStore.getHeroSeries()
		}
		emit("heroLoaded")
	})

	/**
	 * A computed property to get the combined hero section items from movies and series.
	 */
	const heroData: ComputedRef<(Movie | Series)[]> = computed(() => {
		if (route.name === "MoviesPage") {
			return movieStore.state.heroMovies
		} else if (route.name === "SeriesPage") {
			return seriesStore.state.heroSeries
		}

		// Home route - return both
		return [...movieStore.state.heroMovies, ...seriesStore.state.heroSeries]
	})

	watch(
		() => teaserStore.heroSectionState.isMuted,
		(newVal) => {
			if (player) {
				player.muted(newVal)
			}
		}
	)

	watch(
		() => heroData.value,
		async (newVal) => {
			let videoUrl =
				newVal[0]?.field_trailer_file.data !== null
					? `${url}${newVal[0]?.field_trailer_file.uri.url}`
					: newVal[0]?.field_trailer?.uri
					? newVal[0]?.field_trailer?.uri
					: newVal[0]?.field_trailer
			const res = await api.get("/api/sign-url", {
				params: {
					uuid: newVal[0].id,
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			})
			videoUrl = res.data.signed_url
			if (newVal.length > 0) {
				options = {
					autoplay: true,
					muted: teaserStore.heroSectionState.isMuted,
					language: "el",
					// poster: newVal[0]?.field_hero_image.image_style_uri.teaser_hero,
					src: videoUrl,
					controls: false,
					loop: false,
					playsinline: true,
					disablePictureInPicture: true,
					spatialNavigation: {
						enabled: true,
						horizontalSeek: true,
					},
				}

				emit("activeItemChanged", newVal[0])
				teaserStore.heroSectionState.activeItem = newVal[0]
				initPlayer()
			}
			// Place pagination after DOM updated
			await placePagination()
		},
		{ once: true }
	)

	const initPlayer = () => {
		// Initialize the player
		// setTimeout(() => {

		if (options.src === null) {
			return
		}

		window.initPlayer(containerId, options, () => {
			player = window.kwikMotion(containerId)
			player.muted(teaserStore.heroSectionState.isMuted)

			setTimeout(() => {
				hideImage.value = true
			}, 500)

			// Remove old event listener if it exists
			if (endedHandler) {
				player.off("ended", endedHandler)
			}

			// Create and store the event handler
			endedHandler = () => {
				if (splideRef.value) {
					// Move to next slide
					splideRef.value.splide.go(">")
					// Update slide index with loop reset
					// currentSlideIndex.value = (currentSlideIndex.value + 1) % heroData.value.length

					// Update player source
					// const nextItem = heroData.value[currentSlideIndex.value]
					// const nextSrc =
					// 	nextItem?.field_trailer_file.data !== null
					// 		? `${url}/${nextItem?.field_trailer_file.uri.url}`
					// 		: nextItem?.field_trailer
					// 		? nextItem?.field_trailer?.uri
					// 		: null

					// if (nextSrc === null) {
					// 	return
					// }
					// player.src(nextSrc)
					// player.poster(nextItem?.field_hero_image?.image_style_uri.teaser_hero)
					// player.play()
				}
			}

			player.on("ended", endedHandler)
			// Setup intersection observer
			setupIntersectionObserver()
		})
		// }, 1500)
	}

	const handlePaginationUpdate = async (data, next, curr) => {
		// Listen to splide move event
		// initPlayer()
		if (splideRef.value?.splide) {
			const activeIndex = splideRef.value.splide.index
			if (currentSlideIndex.value === activeIndex) {
				return
			}
			// splideRef.value.splide.on("pagination:updated", (newIndex: number) => {
			currentSlideIndex.value = activeIndex
			const currentItem = heroData.value[activeIndex]

			emit("activeItemChanged", currentItem)
			teaserStore.heroSectionState.activeItem = currentItem
			// Update player source when slide changes

			const res = await api.get("/api/sign-url", {
				params: {
					uuid: currentItem.id,
				},
				headers: {
					Accept: "application/vnd.api+json",
				},
			})
			const videoUrl = res.data.signed_url

			// const currentSrc =
			// 	currentItem?.field_trailer_file.data !== null
			// 		? `${url}${currentItem?.field_trailer_file.uri.url}`
			// 		: currentItem?.field_trailer
			// 		? currentItem?.field_trailer?.uri
			// 		: null
			// console.log("Updating player source to ", videoUrl)
			// player.pause()
			try {
				if (player) {
					player.dispose()
					player = null
				}
				options.src = videoUrl
				if (videoUrl) {
					initPlayer()
				}
			} catch (e) {
				console.error("Error updating player source:", e)
			}

			// if (currentSrc === null) {
			// 	if (player) {
			// 		// Remove event listener before disposing
			// 		if (endedHandler) {
			// 			player.off("ended", endedHandler)
			// 		}
			// 		player.dispose()
			// 		player = null
			// 		hideImage.value = false
			// 	}
			// 	return
			// }
			// if (player) {
			// 	console.log("player exists, changing source to ", currentSrc)
			// 	player.dispose()
			// 	options.src = currentSrc
			// 	initPlayer()

			// 	// setTimeout(() => {
			// 	// 	if (typeof window.kwikMotion(containerId).source === "function") {
			// 	// 		window.kwikMotion(containerId).source({ src: currentSrc })
			// 	// 	}
			// 	// 	player.pause()
			// 	// }, 1500)
			// 	// player.poster(currentItem?.field_hero_image?.image_style_uri.teaser_hero)

			// 	// setTimeout(() => {
			// 	// 	player.play()
			// 	// }, 3000)
			// } else {
			// 	player.dispose()
			// 	options.src = currentSrc
			// 	initPlayer()
			// }

			// })
		}
	}

	const setupIntersectionObserver = () => {
		if (!heroSectionRef.value) return

		observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (player) {
						if (entry.isIntersecting) {
							player.play()
						} else {
							player.pause()
						}
					}
				})
			},
			{ threshold: 0.5 }
		)

		observer.observe(heroSectionRef.value)
	}

	// Cleanup on unmount
	// onBeforeUnmount(() => {
	// 	observer.disconnect()
	// })
	onBeforeRouteLeave(() => {
		// Dispose player on route leave
		if (player) {
			// Remove event listener before disposing
			if (endedHandler) {
				player.off("ended", endedHandler)
				endedHandler = null
			}
			player.dispose()
			player = null
		}
	})

	/**
	 * Opens the inner page for the given item.
	 * @param e
	 */
	const openInnerPage = (e: any) => {
		if (e?.type?.includes("movie")) {
			router.push({ name: "InnerMovie", params: { id: e?.drupal_internal__nid } })
		} else if (e?.type?.includes("series")) {
			router.push({ name: "InnerSeries", params: { id: e?.drupal_internal__nid } })
		}
	}
</script>

<style scoped lang="scss">
	.hero-section {
		// min-height: 800px;
		position: relative;
		// border-radius: 12px;
		overflow: hidden;
		height: 56.25vw;

		@include br.respond-to(br.$mobile-xlarge) {
			height: 60vh;
		}

		// @media (min-width: 1920px) {
		// 	height: 1080px;
		// }

		// height: 100vh;
		// min-height: 800px;
		// max-height: 800px;
		// @media (max-width: 1560px) {
		// 	max-height: 75vh;
		// 	// height: 500px;
		// 	// height: calc(100vh - 80px);
		// }
		// @media (max-width: 768px) {
		// 	min-height: unset;
		// 	height: 400px;
		// }

		.splide {
			z-index: 2;
			height: 100%;
			& > * {
				height: 100%;
			}
			.teaser-hero {
				height: 100%;
				& > img {
					height: 100%;
				}
			}
		}

		// .splide__arrows {
		// 	opacity: 0;
		// 	transition: opacity 0.5s ease;
		// }

		// &:hover .splide__arrows {
		// 	opacity: 1;
		// }
	}
	.splide__arrow--prev {
		left: 25px !important;
		transform: translateX(0%) !important;
	}
	.splide__arrow--next {
		right: 25px !important;
		transform: none !important;
	}
	.splide__arrow {
		background: rgba($color: #000000, $alpha: 0.6);
		width: 55px;
		height: 55px;
		svg {
			width: 28px;
			flex-shrink: 0;
			height: auto;
		}
	}
	.kwik-player.trailer {
		height: 100% !important;
	}
</style>
